/**
 * PricingPage
 *
 * Full-page pricing view with token packages, FAQs, and feature highlights.
 * Supports cryptocurrency payments via Bcon Global.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Coins,
  Sparkles,
  Check,
  Shield,
  Zap,
  Clock,
  Wallet,
  Cpu,
  AlertTriangle,
  Loader2,
  Package,
  ChevronDown,
  ArrowLeft,
  Copy,
  CheckCircle,
  ExternalLink,
  X,
  Bitcoin,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTokenBalance } from '../../hooks/useTokenBalance';
import { useTranslation } from '../../hooks/useTranslation';
import {
  fetchPackages as apiFetchPackages,
  createCheckout,
  type TokenPackage,
  type PaymentDetails,
  type CurrencyOption,
} from '../../services/paymentApi';
import { TIMEOUTS } from '../../constants/timings';

import { handleError } from '../../utils/errorHandler';
import { publicAsset } from '../../utils/assetUrl';
import { getExplorerUrl } from '../../utils/cryptoExplorer';

// ============================================================================
// Types
// ============================================================================

interface PricingPageProps {
  onNavigateHome: () => void;
  onLoginClick: () => void;
}

// Currency display icons (fallback for currencies from API)
const CURRENCY_ICONS: Record<string, string> = {
  USDT: '💵',
  USDC: '💲',
  ETH: '⟠',
  BTC: '₿',
  SOL: '◎',
  BNB: '🔶',
};

// ============================================================================
// Main Component
// ============================================================================

export function PricingPage({ onNavigateHome, onLoginClick }: PricingPageProps) {
  const { t } = useTranslation();
  const { user, getIdToken } = useAuth();
  const { balance, fullBalance, refresh: refreshBalance } = useTokenBalance();
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [supportedCurrencies, setSupportedCurrencies] = useState<CurrencyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USDT');
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [copied, setCopied] = useState<'address' | 'amount' | null>(null);

  // Fetch packages and supported currencies on mount
  const fetchPackages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiFetchPackages();
      setPackages(result.packages);
      setSupportedCurrencies(result.currencies);
    } catch (err) {
      setError(handleError(err, 'PricingPage', 'Failed to load packages'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
    if (user) {
      refreshBalance();
    }
  }, [fetchPackages, refreshBalance, user]);

  // Handle purchase
  const handlePurchase = async (packageId: string) => {
    if (!user) {
      onLoginClick();
      return;
    }

    setPurchasing(packageId);
    setError(null);

    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const details = await createCheckout({
        packageId,
        authToken: token,
        successUrl: `${window.location.origin}/tokens/success`,
        cancelUrl: `${window.location.origin}/pricing`,
        currency: selectedCurrency,
      });

      setPaymentDetails(details);
    } catch (err) {
      setError(handleError(err, 'PricingPage', 'Failed to create payment'));
    } finally {
      setPurchasing(null);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string, type: 'address' | 'amount') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), TIMEOUTS.COPY_FEEDBACK);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(type);
      setTimeout(() => setCopied(null), TIMEOUTS.COPY_FEEDBACK);
    }
  };

  // Calculate days until next expiration
  const daysUntilExpiration = fullBalance?.next_expiration
    ? Math.ceil(
        (new Date(fullBalance.next_expiration).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary-50 to-white dark:from-secondary-900 dark:to-secondary-800">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Back Button */}
        <button
          onClick={onNavigateHome}
          className="inline-flex items-center gap-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('pricing.backToApp')}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl mb-4">
            <Coins className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-secondary-900 dark:text-white mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-secondary-600 dark:text-secondary-400 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Crypto Coming Soon Banner */}
        {!loading && packages.some((p) => p.available === false) && (
          <div className="max-w-2xl mx-auto mb-10">
            <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 dark:bg-amber-800/50 rounded-xl">
                  <Bitcoin className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-800 dark:text-amber-200">
                    {t('pricing.cryptoUpgradeTitle') || 'Upgrading to Crypto Payments'}
                  </h3>
                  <p className="text-amber-700 dark:text-amber-300 mt-1">
                    {t('pricing.cryptoUpgradeMessage') ||
                      "We're integrating Bitcoin, Ethereum, Solana, and stablecoins (USDT, USDC). Token purchases will return very soon!"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Balance Card */}
        {user && (
          <div className="max-w-md mx-auto mb-10">
            <div className="bg-white dark:bg-secondary-800 rounded-2xl p-6 shadow-lg border border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between mb-3">
                <span className="text-secondary-600 dark:text-secondary-400">
                  {t('pricing.yourBalance')}
                </span>
                <span className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {balance === null ? '—' : balance.toLocaleString()} {t('common.tokens')}{' '}
                  <img
                    src={publicAsset('token-coin-32.png')}
                    alt=""
                    className="h-5 w-5 inline"
                    draggable={false}
                  />
                </span>
              </div>
              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                {balance === null
                  ? t('tokens.balanceUnavailable')
                  : `~$${(balance / 1000).toFixed(2)} USD ${t('pricing.value')}`}
              </p>

              {/* Expiration Warning */}
              {daysUntilExpiration !== null &&
                daysUntilExpiration <= 7 &&
                fullBalance?.expiring_soon_amount && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    <span className="text-sm text-amber-700 dark:text-amber-400">
                      {t('pricing.tokensExpiring', {
                        count: fullBalance.expiring_soon_amount.toLocaleString(),
                        days: daysUntilExpiration,
                      })}
                    </span>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400 text-center">
            {error}
            <button
              onClick={fetchPackages}
              className="ml-2 underline hover:no-underline font-medium"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        )}

        {/* Currency Selector */}
        {!loading && !paymentDetails && supportedCurrencies.length > 0 && (
          <div className="max-w-2xl mx-auto mb-8">
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-3 text-center">
              {t('pricing.selectCurrency') || 'Select Payment Currency'}
            </label>
            <div className="flex flex-wrap justify-center gap-2">
              {supportedCurrencies.map((crypto) => (
                <button
                  key={crypto.code}
                  onClick={() => setSelectedCurrency(crypto.code)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedCurrency === crypto.code
                      ? 'bg-primary-500 text-white'
                      : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
                  }`}
                >
                  <span>{CURRENCY_ICONS[crypto.code] || '💰'}</span>
                  <span>{crypto.code}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Cards or Payment Details */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary-500" />
          </div>
        ) : paymentDetails ? (
          <PaymentDetailsCard
            details={paymentDetails}
            copied={copied}
            onCopy={copyToClipboard}
            onClose={() => setPaymentDetails(null)}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {packages.map((pkg) => (
              <PricingCard
                key={pkg.id}
                pkg={pkg}
                selectedCurrency={selectedCurrency}
                onPurchase={handlePurchase}
                purchasing={purchasing === pkg.id}
                disabled={purchasing !== null}
              />
            ))}
          </div>
        )}

        {/* Features Grid */}
        {!paymentDetails && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto mb-12">
            <FeatureItem
              icon={<Check className="h-5 w-5" />}
              text={t('pricing.features.neverExpire')}
            />
            <FeatureItem
              icon={<Shield className="h-5 w-5" />}
              text={t('pricing.features.securePayment')}
            />
            <FeatureItem
              icon={<Zap className="h-5 w-5" />}
              text={t('pricing.features.instantDelivery')}
            />
            <FeatureItem
              icon={<Clock className="h-5 w-5" />}
              text={t('pricing.features.noSubscription')}
            />
            <FeatureItem
              icon={<Cpu className="h-5 w-5" />}
              text={t('pricing.features.localGpuDiscount')}
            />
            <FeatureItem icon={<Wallet className="h-5 w-5" />} text="Crypto Payments" />
          </div>
        )}

        {/* FAQ Section */}
        {!paymentDetails && (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white text-center mb-8">
              {t('pricing.faq.title')}
            </h2>
            <div className="space-y-4">
              <FAQItem
                question={t('pricing.faq.howMany.question')}
                answer={t('pricing.faq.howMany.answer')}
              />
              <FAQItem
                question={t('pricing.faq.expire.question')}
                answer={t('pricing.faq.expire.answer')}
              />
              <FAQItem
                question="How do crypto payments work?"
                answer="After selecting a package, you'll receive a unique wallet address. Send the exact amount shown in your preferred cryptocurrency. Once the transaction is confirmed on the blockchain (usually within a few minutes), your tokens will be automatically credited to your account."
              />
              <FAQItem
                question={t('pricing.faq.localGpu.question')}
                answer={t('pricing.faq.localGpu.answer')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface PricingCardProps {
  pkg: TokenPackage;
  selectedCurrency: string;
  onPurchase: (id: string) => void;
  purchasing: boolean;
  disabled: boolean;
}

function PricingCard({
  pkg,
  selectedCurrency,
  onPurchase,
  purchasing,
  disabled,
}: PricingCardProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`relative bg-white dark:bg-secondary-800 rounded-2xl border-2 transition-all hover:shadow-xl p-5 sm:p-6 ${
        pkg.popular
          ? 'border-primary-500 shadow-lg shadow-primary-500/10'
          : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-300 dark:hover:border-primary-600'
      }`}
    >
      {/* Popular Badge */}
      {pkg.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 bg-primary-500 text-white text-xs sm:text-sm font-semibold rounded-full shadow-lg whitespace-nowrap">
            {t('pricing.mostPopular')}
          </span>
        </div>
      )}

      {/* Package header */}
      <div className="flex items-center gap-2 mb-3 pt-2">
        <Package className="h-5 w-5 text-primary-500" />
        <h3 className="font-semibold text-secondary-900 dark:text-white uppercase tracking-wide">
          {pkg.name}
        </h3>
      </div>

      {/* Token Amount */}
      <div className="text-center mb-2">
        <span className="text-3xl sm:text-4xl font-bold text-secondary-900 dark:text-white">
          {pkg.tokens.toLocaleString()}
        </span>
        <span className="text-secondary-500 dark:text-secondary-400 ml-2 inline-flex items-center gap-1">
          {t('common.tokens')}
          <img
            src={publicAsset('token-coin-32.png')}
            alt=""
            className="h-5 w-5"
            draggable={false}
          />
        </span>
      </div>

      {/* Discount badge */}
      {pkg.discount_percent > 0 && (
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium rounded-full">
            <Sparkles className="h-3.5 w-3.5" />
            {t('pricing.savePercent', { percent: pkg.discount_percent })}
          </span>
        </div>
      )}

      {/* Price */}
      <div className="text-center mb-6">
        <span className="text-2xl sm:text-3xl font-bold text-secondary-900 dark:text-white">
          ${pkg.price_usd.toFixed(2)}
        </span>
        <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
          ${pkg.price_per_1000.toFixed(2)} {t('pricing.perThousand')}
        </p>
      </div>

      {/* Buy Button */}
      <button
        onClick={() => pkg.available !== false && onPurchase(pkg.id)}
        disabled={disabled || pkg.available === false}
        className={`w-full py-3 px-4 rounded-xl font-semibold transition-all min-h-[44px] ${
          pkg.available === false
            ? 'bg-secondary-100 dark:bg-secondary-800 cursor-not-allowed text-secondary-400 dark:text-secondary-500'
            : purchasing
              ? 'bg-secondary-200 dark:bg-secondary-700 cursor-wait text-secondary-500'
              : pkg.popular
                ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-lg shadow-primary-500/25'
                : 'bg-secondary-100 dark:bg-secondary-700 hover:bg-secondary-200 dark:hover:bg-secondary-600 text-secondary-900 dark:text-white'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {pkg.available === false ? (
          <span className="flex items-center justify-center gap-2">
            <Bitcoin className="h-5 w-5" />
            {t('pricing.cryptoComingSoon') || 'Crypto Soon'}
          </span>
        ) : purchasing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('pricing.processing')}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Wallet className="h-4 w-4" />
            Pay with {selectedCurrency}
          </span>
        )}
      </button>
    </div>
  );
}

interface PaymentDetailsCardProps {
  details: PaymentDetails;
  copied: 'address' | 'amount' | null;
  onCopy: (text: string, type: 'address' | 'amount') => void;
  onClose: () => void;
}

function PaymentDetailsCard({ details, copied, onCopy, onClose }: PaymentDetailsCardProps) {
  const { t } = useTranslation();
  return (
    <div className="max-w-xl mx-auto mb-12">
      <div className="bg-white dark:bg-secondary-800 rounded-2xl p-6 sm:p-8 shadow-xl border border-secondary-200 dark:border-secondary-700">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-secondary-900 dark:text-white">
            {t('pricing.paymentDetails.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
          >
            <X className="h-5 w-5 text-secondary-500" />
          </button>
        </div>

        {/* Package Info */}
        <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400">
                {t('pricing.paymentDetails.package')}
              </p>
              <p className="text-lg font-bold text-primary-900 dark:text-primary-100">
                {details.package_name}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-primary-600 dark:text-primary-400">
                {t('pricing.paymentDetails.youGet')}
              </p>
              <p className="text-lg font-bold text-primary-900 dark:text-primary-100">
                {details.package_tokens.toLocaleString()} {t('pricing.paymentDetails.tokensSuffix')}{' '}
                <img
                  src={publicAsset('token-coin-32.png')}
                  alt=""
                  className="h-4 w-4 inline"
                  draggable={false}
                />
              </p>
            </div>
          </div>
        </div>

        {/* Payment Instructions */}
        <div className="space-y-4">
          <div className="flex-row-muted">
            <div className="badge-step">1</div>
            <span className="font-medium">{t('pricing.paymentDetails.step1')}</span>
          </div>

          <div className="p-4 bg-secondary-100 dark:bg-secondary-700 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {details.payment_amount} {details.payment_currency}
                </p>
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  ≈ ${details.price_usd.toFixed(2)} USD
                </p>
              </div>
              <button
                onClick={() => onCopy(details.payment_amount, 'amount')}
                className="p-3 rounded-lg bg-white dark:bg-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-500 transition-colors"
              >
                {copied === 'amount' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <Copy className="h-5 w-5 text-secondary-500" />
                )}
              </button>
            </div>
          </div>

          <div className="flex-row-muted">
            <div className="badge-step">2</div>
            <span className="font-medium">
              {t('pricing.paymentDetails.step2', { chain: details.chain })}
            </span>
          </div>

          <div className="p-4 bg-secondary-100 dark:bg-secondary-700 rounded-xl">
            <div className="flex items-center gap-3">
              <code className="flex-1 text-sm font-mono text-secondary-900 dark:text-white break-all">
                {details.payment_address}
              </code>
              <button
                onClick={() => onCopy(details.payment_address, 'address')}
                className="p-3 rounded-lg bg-white dark:bg-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-500 transition-colors flex-shrink-0"
              >
                {copied === 'address' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <Copy className="h-5 w-5 text-secondary-500" />
                )}
              </button>
            </div>
          </div>

          <div className="flex-row-muted">
            <div className="badge-step">3</div>
            <span className="font-medium">{t('pricing.paymentDetails.step3')}</span>
          </div>

          <p className="text-sm text-secondary-500 dark:text-secondary-400 pl-8">
            {t('pricing.paymentDetails.creditedAutomatically')}
          </p>
        </div>

        {/* Warning */}
        <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>{t('pricing.paymentDetails.importantLabel')}</strong>{' '}
            {t('pricing.paymentDetails.networkWarning', {
              currency: details.payment_currency,
              chain: details.chain,
            })}
          </p>
        </div>

        {/* Explorer Link — correct URL per chain (SSOT: cryptoExplorer) */}
        <a
          href={getExplorerUrl(details.chain, details.payment_address)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex items-center justify-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          <ExternalLink className="h-4 w-4" />
          {t('pricing.paymentDetails.viewOnExplorer')}
        </a>
      </div>
    </div>
  );
}

function FeatureItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex flex-col items-center text-center p-3">
      <div className="text-primary-500 mb-2">{icon}</div>
      <span className="text-xs text-secondary-600 dark:text-secondary-400">{text}</span>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 sm:px-6 py-4 text-left flex items-center justify-between bg-white dark:bg-secondary-800 hover:bg-secondary-50 dark:hover:bg-secondary-750 transition-colors min-h-[44px]"
      >
        <span className="font-medium text-secondary-900 dark:text-white pr-4">{question}</span>
        <ChevronDown
          className={`h-5 w-5 text-secondary-400 flex-shrink-0 transform transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 sm:px-6 py-4 bg-secondary-50 dark:bg-secondary-850 text-secondary-600 dark:text-secondary-400">
          {answer}
        </div>
      )}
    </div>
  );
}

export default PricingPage;
